import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'

import Logger from 'core/utils/Logger'

import { fetchPlanningWizardSteps } from '../../api/fetchPlanningWizardSteps'
import { findFirstIncompleteWizardStep } from '../../helpers/findFirstIncompleteWizardStep'
import { findNextWizardStep } from '../../helpers/findNextWizardStep'
import { IWizardStep, IWizardStepsGroup } from '../../models/IWizardStep'

import { initialState } from './config'
import { PLANNING_WIZARD_NAMESPACE } from './constants'

interface IFetchWizardStepsParams {
  partnerId: string
  fYear: number
  programType: ProgramTypes
}

const sortWizardData = (
  data: (IWizardStep | IWizardStepsGroup)[]
): (IWizardStep | IWizardStepsGroup)[] => {
  const sorted = data.sort((a, b) => {
    const orderA = 'grouporder' in a ? a.grouporder : 'steporder' in a ? a.steporder : 0
    const orderB = 'grouporder' in b ? b.grouporder : 'steporder' in b ? b.steporder : 0

    return orderA - orderB
  })

  return sorted.map((item) => {
    if ('steps' in item) {
      return {
        ...item,
        steps: item.steps.sort((a, b) => a.steporder - b.steporder),
      }
    }

    return item
  })
}

export const requestWizardSteps = createAsyncThunk(
  `${PLANNING_WIZARD_NAMESPACE}/requestWizardSteps`,
  async (params: IFetchWizardStepsParams) => {
    let data: (IWizardStep | IWizardStepsGroup)[] = []

    try {
      const response = await fetchPlanningWizardSteps(params)
      data = JSON.parse(response)
      data = sortWizardData(data)
    } catch (e: any) {
      Logger.log(e)
    }

    return data
  }
)

const planningWizardSlice = createSlice({
  name: PLANNING_WIZARD_NAMESPACE,
  initialState,
  reducers: {
    openWizard: (state) => {
      state.isVisible = true
      state.wasOpened = true
      if (!state.activeStepId) {
        state.activeStepId = findFirstIncompleteWizardStep(state.wizardSteps)
      }
    },
    closeWizard: (state) => {
      state.isVisible = false
    },
    setWizardSteps: (state, action: PayloadAction<(IWizardStep | IWizardStepsGroup)[]>) => {
      state.wizardSteps = action.payload
      state.activeStepId = findFirstIncompleteWizardStep(action.payload)
    },
    setActiveStepId: (state, action: PayloadAction<string>) => {
      state.activeStepId = action.payload
    },
    changeWizardStep: (state, action: PayloadAction<{ newStepId: string }>) => {
      state.activeStepId = action.payload.newStepId
    },
    setWasOpened: (state, action: PayloadAction<boolean>) => {
      state.wasOpened = action.payload
    },
    goToNextStep: (state) => {
      if (!state.activeStepId) {
        return
      }

      const nextStepId = findNextWizardStep(state.wizardSteps, state.activeStepId)

      if (nextStepId) {
        state.activeStepId = nextStepId
      } else {
        state.isVisible = false
      }
    },
    setWizardSaveDisabled: (state, action: PayloadAction<boolean>) => {
      state.isSaveDisabled = action.payload
    },
    setIsHideWizard: (state, action: PayloadAction<boolean>) => {
      state.isHideWizard = action.payload
    },
    setWizardIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    updateStep: (
      state,
      action: PayloadAction<{ updatedStep: Partial<IWizardStep> & { id: string } }>
    ) => {
      const { updatedStep } = action.payload
      const updateStepInArray = (steps: (IWizardStep | IWizardStepsGroup)[]): boolean => {
        for (let i = 0; i < steps.length; i++) {
          const item = steps[i]
          if ('steps' in item) {
            // This is a group
            if (updateStepInArray(item.steps)) return true
          } else {
            // This is a step
            if (item.id === updatedStep.id) {
              steps[i] = { ...item, ...updatedStep }
              return true
            }
          }
        }
        return false
      }
      updateStepInArray(state.wizardSteps)
    },
    resetPlanningWizard: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(requestWizardSteps.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(requestWizardSteps.fulfilled, (state, action) => {
        state.isLoading = false
        state.wizardSteps = action.payload
        state.activeStepId = findFirstIncompleteWizardStep(action.payload)
      })
      .addCase(requestWizardSteps.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || 'Failed to fetch wizard steps'
      })
  },
})

export const {
  openWizard,
  closeWizard,
  setWizardSteps,
  setActiveStepId,
  changeWizardStep,
  setWasOpened,
  goToNextStep,
  setWizardSaveDisabled,
  setIsHideWizard,
  setWizardIsLoading,
  updateStep,
  resetPlanningWizard,
} = planningWizardSlice.actions

export default planningWizardSlice.reducer
